import {InfoTooltip, layer} from '@wandb/components';
import {Select, SelectCreatable} from '@wandb/weave/components/Form/Select';
import React, {useEffect, useMemo} from 'react';
import {type GroupBase} from 'react-select';

import {ERROR_BORDER_COLOR} from '../constants';
import {useGetMetricFromSource} from '../hooks';
import {WeaveMetricThresholdActions} from '../store';
import {type SourceType} from '../types';

type MetricPathOption = {
  value: string;
  label: string;
};

type MetricPathGroup = GroupBase<MetricPathOption>;

const GROUP_ORDER = ['output', 'summary', 'inputs', 'attributes', 'other'];
const GROUP_LABELS: Record<string, string> = {
  output: 'Output',
  summary: 'Summary',
  inputs: 'Inputs',
  attributes: 'Attributes',
  other: 'Other',
};

const groupMetricOptions = (keys: string[]): MetricPathGroup[] => {
  const groups: Record<string, MetricPathOption[]> = {};
  for (const key of keys) {
    const prefix = key.split('.')[0];
    const groupKey = GROUP_ORDER.includes(prefix) ? prefix : 'other';
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push({value: key, label: key});
  }
  return GROUP_ORDER.filter(g => groups[g]?.length > 0).map(g => ({
    label: GROUP_LABELS[g],
    options: groups[g],
  }));
};

type MetricPathSelectorProps = {
  entityName: string;
  projectName: string;
  sourceType: SourceType;
  opStr: string;
  monitorRef: string;
  selectedPath: string;
  onPathChange: (value: string) => void;
  hasError: boolean;
};

export const MetricPathSelector = ({
  entityName,
  projectName,
  sourceType,
  opStr,
  monitorRef,
  selectedPath,
  onPathChange,
  hasError,
}: MetricPathSelectorProps) => {
  const {allKeys, loading, scorerOpRef} = useGetMetricFromSource(
    entityName,
    projectName,
    sourceType,
    opStr,
    monitorRef
  );

  // When a monitor scorer's sample call is fetched, store its op ref
  // (e.g. "weave:///e/p/op/LLMAsAJudgeScorer.score:hash") so the trigger
  // payload can include it as op_str for efficient server-side filtering.
  useEffect(() => {
    WeaveMetricThresholdActions.setScorerOpStr(scorerOpRef);
  }, [scorerOpRef]);

  // Ensure the currently selected path is always in the list (e.g. while
  // the call data is still loading during edit)
  const keysWithSelected = useMemo(() => {
    if (selectedPath && !allKeys.includes(selectedPath)) {
      return [...allKeys, selectedPath].sort();
    }
    return allKeys;
  }, [allKeys, selectedPath]);

  const groupedOptions = useMemo(
    () => groupMetricOptions(keysWithSelected),
    [keysWithSelected]
  );

  const selectedOption: MetricPathOption | null = selectedPath
    ? {value: selectedPath, label: selectedPath}
    : null;

  const handleChange = (newValue: MetricPathOption | null) => {
    onPathChange(newValue?.value || '');
  };

  // Disabled when no source is selected
  const hasSource = sourceType === 'OPERATION' ? !!opStr : !!monitorRef;
  const isDisabled = !hasSource;

  // Show info message when the selected source has no executed calls
  const hasNoData = hasSource && !loading && groupedOptions.length === 0;

  const selectStyles = {
    container: (base: any) => ({...base, minWidth: '190px'}),
    menu: (base: any) => ({...base, zIndex: layer('menu')}),
    menuPortal: (base: any) => ({...base, zIndex: layer('menu')}),
    control: (base: any) => ({
      ...base,
      borderColor: hasError ? ERROR_BORDER_COLOR : base.borderColor,
    }),
  };

  // Use creatable select for monitors to allow manual input
  const SelectComponent = sourceType === 'MONITOR' ? SelectCreatable : Select;

  return (
    <div className="flex flex-col gap-4">
      <SelectComponent<MetricPathOption, false, MetricPathGroup>
        value={selectedOption}
        options={groupedOptions}
        onChange={handleChange}
        isClearable={true}
        placeholder={
          loading
            ? 'Loading metrics...'
            : hasNoData && sourceType === 'MONITOR'
              ? 'Enter metric path...'
              : 'Select metric path...'
        }
        menuPortalTarget={document.body}
        isDisabled={isDisabled}
        styles={selectStyles}
        formatCreateLabel={(inputValue: string) => `Use "${inputValue}"`}
      />
      {hasNoData && <NoDataHint sourceType={sourceType} />}
    </div>
  );
};

/**
 * Info hint shown when the selected source has no executed calls yet.
 */
const NoDataHint = ({sourceType}: {sourceType: SourceType}) => {
  const isMonitor = sourceType === 'MONITOR';
  return (
    <div className="flex items-center gap-4 text-sm text-wb-text-tertiary">
      <span>
        No data found for selected {isMonitor ? 'monitor' : 'op'}
        {isMonitor ? ' - enter metric path manually' : ''}
      </span>
      <InfoTooltip
        id="source-no-data-hint"
        aria-label="No data hint"
        placement="bottom">
        {isMonitor
          ? 'This monitor has not executed yet. Either wait for at least one successful score or enter the metric path manually (e.g., output.score).'
          : 'This op has no logged calls yet. Run the op at least once so metric paths can be discovered from its output.'}
      </InfoTooltip>
    </div>
  );
};
